import { useState, useEffect } from "react";
import { Building2, TrendingUp, Users, RefreshCw, ExternalLink, Search, Star, ChevronDown, ChevronRight, Crosshair, Calendar } from "lucide-react";
import { investorsAPI } from "../services/api";

export default function CopyTradeTab({ onAnalyzeTicker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFund, setExpandedFund] = useState(null);
  const [fundHoldings, setFundHoldings] = useState({});
  const [loadingFund, setLoadingFund] = useState(null);
  const [trackTicker, setTrackTicker] = useState("");
  const [trackResult, setTrackResult] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await investorsAPI.getAll13F();
      setData(res);
    } catch (e) {
      console.error("13F fetch error:", e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const loadFundDetail = async (fundKey) => {
    if (expandedFund === fundKey) { setExpandedFund(null); return; }
    setExpandedFund(fundKey);
    if (fundHoldings[fundKey]) return;
    setLoadingFund(fundKey);
    try {
      const res = await investorsAPI.get13FHoldings(fundKey);
      setFundHoldings(prev => ({ ...prev, [fundKey]: res }));
    } catch (e) { console.error("Fund detail error:", e); }
    setLoadingFund(null);
  };

  const handleTrack = async () => {
    const t = trackTicker.trim().toUpperCase();
    if (!t) return;
    setTrackLoading(true);
    try {
      const res = await investorsAPI.trackTicker(t);
      setTrackResult(res);
    } catch (e) { console.error("Track error:", e); }
    setTrackLoading(false);
  };

  const fmtValue = (v) => {
    if (!v) return "$0";
    if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
    if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
    if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
    return `$${v.toLocaleString()}`;
  };

  const fmtDate = (d) => {
    if (!d || d === "N/A") return "N/A";
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
    } catch { return d; }
  };

  const funds = data?.funds || {};
  const consensus = data?.consensus || [];
  const fundEntries = Object.entries(funds);

  return (
    <div className="space-y-4" data-testid="copy-trade-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-bold">Copy Trade — 13F Institutionnels</h2>
          <span className="text-xs text-slate-400">SEC Filings</span>
        </div>
        <button onClick={fetchAll} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs hover:bg-violet-500/30 transition-colors" data-testid="refresh-13f">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      {/* TRACKER: Search for a ticker across all funds */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Crosshair className="w-4 h-4 text-rose-400" />
          <span className="text-sm font-bold">Tracker un Ticker</span>
          <span className="text-xs text-slate-500">Cherchez si un fonds institutionnel detient cette action</span>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <input
              type="text"
              value={trackTicker}
              onChange={(e) => setTrackTicker(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleTrack()}
              placeholder="Ex: ASTS, NBIS, PLTR..."
              className="w-full pl-8 pr-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/50"
              data-testid="track-ticker-input"
            />
          </div>
          <button
            onClick={handleTrack}
            disabled={trackLoading || !trackTicker.trim()}
            className="px-4 py-2 rounded-lg bg-rose-500/20 border border-rose-500/30 text-rose-300 text-xs font-bold hover:bg-rose-500/30 transition-colors disabled:opacity-50"
            data-testid="track-ticker-btn"
          >
            {trackLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Tracker"}
          </button>
        </div>

        {/* Track results */}
        {trackResult && (
          <div className="mt-3 p-3 rounded-lg bg-slate-800/40 border border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-sm font-bold text-cyan-400">{trackResult.ticker}</span>
              {trackResult.found_in_funds > 0 ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold">
                  {trackResult.found_in_funds} fonds detectes
                </span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-600/30 text-slate-400 border border-slate-500/20">
                  Aucun fonds institutionnel detecte
                </span>
              )}
              {trackResult.total_institutional_value > 0 && (
                <span className="text-xs text-emerald-400 font-mono font-bold">{fmtValue(trackResult.total_institutional_value)}</span>
              )}
            </div>
            {trackResult.holdings?.length > 0 ? (
              <div className="space-y-1">
                {trackResult.holdings.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 px-2 py-1.5 rounded bg-slate-700/20">
                    <span className="text-xs font-bold text-white w-[140px] truncate">{h.fund}</span>
                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                      <Calendar className="w-2.5 h-2.5" />
                      {fmtDate(h.filing_date)}
                    </div>
                    <span className="text-xs font-mono text-amber-400 font-bold">{h.weight_pct}%</span>
                    <span className="text-xs font-mono text-slate-300">{h.shares?.toLocaleString()} shares</span>
                    <span className="text-xs font-mono text-emerald-400">{fmtValue(h.value)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-slate-500 py-1">
                Ce ticker n'apparait dans aucun 13F des {fundEntries.length} fonds suivis. Il est possible qu'il soit trop petit ou hors du scope des mega-fonds.
              </div>
            )}
          </div>
        )}
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20 gap-2">
          <RefreshCw className="w-5 h-5 animate-spin text-violet-400" />
          <span className="text-sm text-slate-400">Chargement des filings 13F...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* LEFT: Consensus */}
          <div className="xl:col-span-2 glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-bold">Consensus Institutionnel</span>
              <span className="text-xs text-slate-500">{consensus.length} actions suivies par plusieurs fonds</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700/50">
                    <th className="text-left py-2 px-1 font-medium">#</th>
                    <th className="text-left py-2 px-1 font-medium">Ticker</th>
                    <th className="text-left py-2 px-1 font-medium">Societe</th>
                    <th className="text-center py-2 px-1 font-medium">Fonds</th>
                    <th className="text-right py-2 px-1 font-medium">Valeur Totale</th>
                    <th className="text-right py-2 px-1 font-medium">Shares</th>
                    <th className="text-left py-2 px-1 font-medium">Investisseurs</th>
                  </tr>
                </thead>
                <tbody>
                  {consensus.map((c, i) => (
                    <tr key={c.ticker} className="border-b border-slate-800/30 hover:bg-slate-700/20 cursor-pointer transition-colors" onClick={() => onAnalyzeTicker?.(c.ticker)} data-testid={`consensus-${c.ticker}`}>
                      <td className="py-2 px-1 text-slate-500 font-bold">{i + 1}</td>
                      <td className="py-2 px-1">
                        <span className="font-mono font-bold text-cyan-400 hover:text-cyan-300 flex items-center gap-0.5">
                          {c.ticker}
                          <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                        </span>
                      </td>
                      <td className="py-2 px-1 text-slate-300 truncate max-w-[140px]">{c.issuer}</td>
                      <td className="py-2 px-1 text-center">
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${c.num_funds >= 5 ? "bg-rose-500/20 text-rose-300 border border-rose-500/30" : c.num_funds >= 3 ? "bg-amber-500/15 text-amber-300 border border-amber-500/25" : "bg-slate-600/30 text-slate-300 border border-slate-500/20"}`}>
                          <Users className="w-2.5 h-2.5" />
                          {c.num_funds}
                        </span>
                      </td>
                      <td className="py-2 px-1 text-right font-mono text-emerald-400 font-bold">{fmtValue(c.total_value)}</td>
                      <td className="py-2 px-1 text-right font-mono text-slate-300">{(c.total_shares || 0).toLocaleString()}</td>
                      <td className="py-2 px-1">
                        <div className="flex flex-wrap gap-0.5">
                          {(c.funds || []).slice(0, 4).map((f, j) => (
                            <span key={j} className="text-[8px] px-1 py-px rounded bg-violet-500/15 text-violet-300 border border-violet-500/20 whitespace-nowrap">{f}</span>
                          ))}
                          {(c.funds || []).length > 4 && <span className="text-[8px] text-slate-500">+{c.funds.length - 4}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* RIGHT: Fund list with filing dates */}
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-bold">Fonds ({fundEntries.length})</span>
            </div>
            <div className="space-y-1 max-h-[600px] overflow-y-auto custom-scrollbar">
              {fundEntries.map(([name, info]) => {
                const isExpanded = expandedFund === name;
                const detail = fundHoldings[name];
                const holdings = detail?.top_holdings || [];
                const isLoading = loadingFund === name;
                const filingDate = info?.filing_date;
                return (
                  <div key={name} className="rounded-lg overflow-hidden">
                    <button
                      onClick={() => loadFundDetail(name)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-700/30 transition-colors ${isExpanded ? "bg-slate-700/40" : ""}`}
                      data-testid={`fund-${name.replace(/\s+/g, "-")}`}
                    >
                      {isExpanded ? <ChevronDown className="w-3 h-3 text-violet-400 shrink-0" /> : <ChevronRight className="w-3 h-3 text-slate-500 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-white truncate">{info?.fund || name}</div>
                        <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                          <Calendar className="w-2.5 h-2.5 text-slate-500" />
                          <span>{filingDate ? fmtDate(filingDate) : "N/A"}</span>
                          {info?.total_value > 0 && (
                            <>
                              <span className="text-slate-600">|</span>
                              <span className="text-emerald-400 font-mono">{fmtValue(info.total_value)}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {isLoading && <RefreshCw className="w-3 h-3 animate-spin text-violet-400 shrink-0" />}
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-2">
                        {isLoading ? (
                          <div className="text-[10px] text-slate-400 py-2 text-center">Chargement du portefeuille...</div>
                        ) : holdings.length > 0 ? (
                          <div className="space-y-px">
                            {holdings.slice(0, 15).map((h, i) => (
                              <div key={h.ticker || i}
                                className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-700/30 cursor-pointer transition-colors"
                                onClick={() => onAnalyzeTicker?.(h.ticker)}
                                data-testid={`holding-${h.ticker}`}
                              >
                                <span className="text-[10px] text-slate-500 w-3 font-bold">{i + 1}</span>
                                <span className="font-mono text-[11px] text-cyan-400 font-bold flex-1">{h.ticker || h.name?.slice(0, 8)}</span>
                                {h.weight_pct != null && <span className="text-[10px] text-amber-400 font-mono">{h.weight_pct?.toFixed?.(1) || "?"}%</span>}
                                <span className="text-[10px] text-slate-400 font-mono">{fmtValue(h.value)}</span>
                              </div>
                            ))}
                            {holdings.length > 15 && (
                              <div className="text-[9px] text-slate-500 text-center py-1">+{holdings.length - 15} autres positions</div>
                            )}
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-500 py-2 text-center">Aucun holding charge</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
